/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum(
  "SchedulerApplicationService",
  ["ApplicationServiceBase", "ApplicationServiceFactory"],
  /**
   * @param {gbc} context
   * @param {classes} cls
   */
  function (context, cls) {
    /**
     * Scheduler service.
     * This class is in charge of keeping track of the commands.
     * Stack commands, schedule their execution on the server
     * Manages client side life cycle representation of the node.
     * @class SchedulerApplicationService
     * @memberOf classes
     * @extends classes.ApplicationServiceBase
     */
    class SchedulerApplicationService extends cls.ApplicationServiceBase /** @lends classes.SchedulerApplicationService.prototype */ {
      /**
       * @param {classes.VMApplication} app owner
       */
      constructor(app) {
        super(app);
        /** @type {classes.CommandBase[]} **/
        this._commandsQueue = [];
        /** @type {classes.CommandBase[]} */
        this._failedCommandsQueue = [];
        /** @type {classes.CommandBase[]} */
        this._commandsWhileFrontCallQueue = [];
        /* commands being converted and send as VM event to VM  */
        this._processingCommands = [];
        /** @type {classes.GroupCommand[]} */
        this._currentGroupCommandQueue = [];
        /** @type {number} */
        this._lastCommandTime = 0;
        /** @type {boolean} */
        this._isInNextCommandLoop = false;
        /** @type {classes.LayoutCommand} */
        this._layoutCommand = null;
        /** @type {classes.RestoreFocusCommand} */
        this._restoreFocusCommand = null;
        /** @type {Number} */
        this._startTimeoutId = 0;
      }

      /**
       * @inheritDoc
       */
      destroy() {
        super.destroy(this);
        if (this._startTimeoutId !== 0) {
          clearTimeout(this._startTimeoutId);
          this._startTimeoutId = 0;
        }
        this._processingCommands = [];
        this._commandsQueue = [];
        this._failedCommandsQueue = [];
        this._commandsWhileFrontCallQueue = [];
        this._currentGroupCommandQueue = [];
        this._layoutCommand = null;
        this._restoreFocusCommand = null;
      }

      /**
       * Returns true if the scheduler has command which are executed but not yet validated
       * @returns {boolean}
       */
      hasProcessingCommands() {
        return this._processingCommands.length !== 0;
      }

      /**
       * Returns true if the scheduler has finished to process all post treatment commands
       * - Execute layout command
       * - Restore Focus command
       * @returns {boolean}
       */
      hasPostTreatmentCommands() {
        return this._restoreFocusCommand !== null || this._layoutCommand !== null;
      }

      /**
       * Empty commands in the queue
       */
      emptyCommandsQueue() {
        if (this._commandsQueue.length > 0 || this._commandsWhileFrontCallQueue.length > 0) {
          context.LogService.scheduler.info("Empty commands queue.");
          this._commandsQueue.length = 0;
          this._commandsWhileFrontCallQueue.length = 0;
        }
      }

      /**
       * Returns true if the scheduler has finished to process the command queue.
       * **The command queue don't include the post treatment commands**
       * @returns {boolean}
       */
      hasNoCommandToProcess() {
        return !this.hasProcessingCommands() && !this.hasPendingCommands();
      }

      /**
       * Returns true if the scheduler has finished to process all commands.
       * **Post treatment included**
       * @returns {boolean}
       */
      hasNothingToProcess() {
        return this.hasNoCommandToProcess() && !this.hasPostTreatmentCommands();
      }

      /**
       * Add a command in the scheduler list
       * @param {classes.CommandBase} cmd new command to add to the list
       * @param {boolean} execute if true execute the next command
       * @param {boolean} topOfQueue if true insert the command at the beginning of the queue. Otherwise, by default it's pushed at the end.
       * @returns {classes.CommandBase} the command added in scheduler
       * @private
       */
      _addCommand(cmd, execute = true, topOfQueue = false) {
        if (!this._application) {
          return null;
        }

        // If offline mode is active, avoid adding the command to the scheduler
        if (context.OfflineModeService.isOfflineModeActive()) {
          return null;
        }

        context.LogService.scheduler.log("[app" + this._application.applicationHash + "] " + cmd.__name + " command added", cmd);

        if (this._currentGroupCommandQueue.length > 0) {
          // if there is a current group command is added to this group
          this._currentGroupCommandQueue.at(-1).addCommand(cmd);
          return cmd;
        }

        // if only one command of this type must be added, and there is already an existing command --> do nothing
        if (cmd.isUnique() && this.hasPendingCommands(cmd.__name)) {
          return null;
        }

        // If a FrontCall is processing, queue it in a special buffer
        if (context.FrontCallService.functionCallIsProcessing()) {
          this._commandsWhileFrontCallQueue.push(cmd);
        } else if (topOfQueue) {
          this._commandsQueue.unshift(cmd);
        } else {
          this._commandsQueue.push(cmd);
        }

        if (execute) {
          this._start(cmd);
        }

        return cmd;
      }

      /**
       * @private
       */
      _initFullQueue() {
        let finalCommandsList = this._commandsQueue.slice();
        for (const failedCmd of this._failedCommandsQueue) {
          /*
           * If the command is unique and already in the command list (that means it was called again).
           * We don't use failed command, so it reset the attempt counter
           */
          if (!(failedCmd.isUnique() && finalCommandsList.findIndex((cmdInList) => failedCmd.__name === cmdInList.__name) !== -1)) {
            finalCommandsList.push(failedCmd);
          }
        }
        this._failedCommandsQueue = [];

        finalCommandsList = finalCommandsList.concat(this._commandsWhileFrontCallQueue);
        this._commandsWhileFrontCallQueue = [];

        return finalCommandsList;
      }

      /**
       * Store failed command if the run attempt is under the maximum limit
       * @param {classes.CommandBase} cmd The Command to store
       * @private
       */
      _handleFailedCommand(cmd) {
        if (cmd.retryIfFailed() && cmd.getRunAttempts() < cmd.getMaxRunAttempts()) {
          context.LogService.scheduler.log(`[app${this._application.applicationHash}] ${cmd.__name} command failed.`);
          this._failedCommandsQueue.push(cmd);
        }
      }

      /**
       * Starts scheduler
       * @param {classes.CommandBase} cmd new command just added in the list
       * @private
       */
      _start(cmd = null) {
        if (this._isInNextCommandLoop) {
          // no need to start if it is already running
          return;
        }

        this.emit(context.constants.schedulerEvents.commandProcessStarted);

        if (cmd === null || cmd.executeImmediately()) {
          // if command is send immediately
          this._executeNextCommand();
        } else if (this._startTimeoutId === 0) {
          // we execute it in the next event cycle
          this._startTimeoutId = window.setTimeout(() => {
            this._startTimeoutId = 0;
            this._executeNextCommand();
          });
        }
      }

      /**
       * Execute next command
       * @private
       */
      _executeNextCommand() {
        if (this._destroyed || !this._application) {
          return;
        }

        if (this._mustWaitForFrontCall()) {
          // If blocking frontcall is sent on the first AUI order we must do a layout
          this._executePostTreatmentCommand();
          return;
        }

        const isProcessing = this.hasProcessingCommands() || this._application?.isProcessing();
        if (isProcessing) {
          // VM still processing (e.g. procedural loop with ui.Interface.refresh()): execute the
          // pending layout so each intermediate update gets rendered, even if client commands are
          // queued (they can't be sent while the VM is busy; waiting for them would freeze the
          // rendering after the first frame). Focus restore and process-ended signaling stay
          // deferred until the VM is idle.
          this._executeLayoutCommand(true);
          return;
        }

        this._commandsQueue = this._initFullQueue();

        let events = [];
        while (events.length === 0 && this._commandsQueue.length !== 0) {
          this._isInNextCommandLoop = true;
          let cmd = null;
          do {
            cmd = this._commandsQueue.shift();

            context.LogService.scheduler.log("Executing " + cmd.__name + " command", cmd);

            // Merge beginning commands of the FIFO into cmd;
            while (this._commandsQueue.length !== 0 && cmd.merge(this._commandsQueue[0])) {
              this._commandsQueue.shift();
            }

            // before execute need to updateNode
            // because node can have changed between command creation and command execution
            let commandProcessed = false;
            const executeCommand = cmd.updateNode();
            if (executeCommand) {
              // save existing scheduler list
              const savedCommandsQueue = this._commandsQueue.slice();
              this._commandsQueue = [];

              commandProcessed = cmd.canBeExecuted() && cmd.execute();

              // all the commands created during the command execution must be added at the beginning of commandsQueue
              this._commandsQueue = this._commandsQueue.concat(savedCommandsQueue);

              if (!commandProcessed) {
                context.LogService.scheduler.log("Popping unused " + cmd.__name + " command", cmd);
              } else if (cmd.getVMEvents) {
                // cmd is a CommandVMBase
                const vmEvents = cmd.getVMEvents();
                if (vmEvents && vmEvents.length > 0) {
                  events = events.concat(vmEvents);
                  // if command generate vm events, add it to processingCommands list
                  this._processingCommands.push(cmd);
                }
              }
            } else {
              context.LogService.scheduler.log("Popping outdated " + cmd.__name + " command", cmd);
            }

            if (!commandProcessed) {
              cmd.fail();
              this._handleFailedCommand(cmd);
            }
            // End Executing cmd group
          } while (this._commandsQueue.length !== 0 && !cmd.needsVmSync());
        }

        this._isInNextCommandLoop = false;

        if (events.length > 0) {
          this._sendEventsToVM(events);
          return;
        }

        this._executePostTreatmentCommand();
      }

      /**
       * Checks whether a FrontCall is currently blocking VM communication.
       *
       * While a FrontCall is in progress, no VM events must be sent,
       * otherwise the client and VM may desynchronize.
       *
       * @returns {boolean} true if scheduler must pause execution
       */
      _mustWaitForFrontCall() {
        if (context.FrontCallService.functionCallIsProcessing() && !this.isRunningLog()) {
          context.LogService.scheduler.log("VM is waiting for a FrontCall response. Waiting...");
          // This is needed for QAService, because when a FV displays a blocking window like FilePicker
          // We need to inform qA service that the command process is finished to not block on te.waitForVM
          this.emit(context.constants.schedulerEvents.commandProcessEnded);
          return true;
        }
        return false;
      }

      /**
       * Sends accumulated VM events to the VM and
       * switches the scheduler to WAITING_VM state.
       *
       * @param {Array} events VM events to send
       */
      _sendEventsToVM(events) {
        this._lastCommandTime = this._processingCommands.at(-1).getTime();

        context.LogService.scheduler.log("Sending events to VM", events);
        this._application.protocolInterface.event(events);
      }

      /**
       * Execute the pending layout command.
       * @param {boolean} [ignorePendingCommands] - when true (VM still processing), run the layout
       *   even if client commands are queued: they can't be sent while the VM is busy, so waiting
       *   for them would starve intermediate rendering during a refresh() loop. Commands already
       *   sent to the VM and awaiting validation still defer it.
       * @private
       */
      _executeLayoutCommand(ignorePendingCommands = false) {
        const canExecute = ignorePendingCommands ? !this.hasProcessingCommands() : this.hasNoCommandToProcess();
        if (canExecute && this._layoutCommand) {
          this._layoutCommand.execute();
          this._layoutCommand = null;
        }
      }

      /**
       * Execute post treatment commands
       * @private
       */
      _executePostTreatmentCommand() {
        this._executeLayoutCommand();

        // if there is nothing more to process, execute restoreFocus command
        if (this.hasNoCommandToProcess()) {
          if (this._restoreFocusCommand) {
            // Safari: needs layout flush before initial focus (GBC-2294).
            if (window.browserInfo.isSafari) {
              const cmd = this._restoreFocusCommand;
              this._restoreFocusCommand = null;
              window.requestAnimationFrame(() => {
                if (this._application && !this._application.isDestroyed()) {
                  cmd.execute();
                }
              });
            } else {
              this._restoreFocusCommand.execute();
              this._restoreFocusCommand = null;
            }
          }
          this.emit(context.constants.schedulerEvents.commandProcessEnded);
        }
      }

      /**
       * Validate the last commands send to the VM
       * This function is called each time we received a message from VM
       * @param {boolean} isNewTask - message send by VM is a new task (new app)
       */
      validateLastCommand(isNewTask = false) {
        if (isNewTask) {
          // no need to anything in this case juste empty the processing commands
          this._processingCommands = [];
          return;
        }

        let needsRefreshLayout = false;
        if (this._processingCommands.length !== 0) {
          context.LogService.scheduler.log("Command(s) validation", this._processingCommands);
          // check if commands request a refreshLayout
          while (this._processingCommands.length > 0) {
            const cmd = this._processingCommands.shift();
            needsRefreshLayout = needsRefreshLayout || cmd.needsRefreshLayout();
          }
        } else {
          context.LogService.scheduler.log("No command to validate");
          needsRefreshLayout = true;
        }

        // require layout after each command validated
        this.layoutCommand({
          noLayout: !needsRefreshLayout
        });

        // try to send next commands
        this._executeNextCommand();
      }

      /**
       * Checks if the given node has pending VALUE command in the scheduler command list
       * @param {classes.NodeBase} [node]
       * @returns {boolean}
       */
      hasPendingScrollCommands(node = null) {
        for (const cmd of this._commandsQueue) {
          if (cmd instanceof cls.ScrollVMCommand && (!node || cmd.getNode() === node)) {
            return true;
          }
        }
        return false;
      }

      /**
       * Returns true if processing commands queue contains at least one ScrollVMCommand
       * @param {classes.NodeBase} [node]
       * @returns {boolean}
       */
      isProcessingScrollCommand(node = null) {
        for (const cmd of this._processingCommands) {
          if (cmd instanceof cls.ScrollVMCommand && (!node || cmd.getNode() === node)) {
            return true;
          }
        }
        return false;
      }

      /**
       * Returns true if processing commands queue contains at least one PageSizeVMCommand
       * @param {classes.NodeBase} [node]
       * @returns {boolean}
       */
      isProcessingPageSizeVMCommand(node = null) {
        return this._processingCommands.some((cmd) => cmd instanceof cls.PageSizeVMCommand && (!node || cmd.getNode() === node));
      }

      /**
       * Returns true if there are some scroll commands in the commands queue or if a scroll command is processing
       * @param {classes.NodeBase} [node]
       * @returns {boolean}
       */
      hasScrollCommandsToProcess(node = null) {
        return this.isProcessingScrollCommand(node) || this.hasPendingScrollCommands(node);
      }

      /**
       * Checks if the given node has pending navigation command in the scheduler command list
       * @returns {boolean}
       */
      hasPendingNavigationCommands() {
        for (const cmd of this._commandsQueue) {
          if (
            (cmd instanceof cls.CurrentRowVMCommand || cmd instanceof cls.FocusVMCommand) &&
            (cls.ActionNode.isTableNavigationAction(cmd._actionName) || cls.ActionNode.isFieldNavigationAction(cmd._actionName))
          ) {
            return true;
          }
        }
        return false;
      }

      /**
       * Checks if the given node has pending FunctionCallResult command in the scheduler command list
       * @param {boolean} [processing] - check also if there is a frontcall command in processing commands list
       * @returns {boolean}
       */
      hasPendingFunctionCallResultCommands(processing) {
        for (const cmd of this._commandsQueue) {
          if (cmd instanceof cls.FunctionCallResultVMCommand) {
            return true;
          }
        }
        if (processing) {
          for (const cmd of this._processingCommands) {
            if (cmd instanceof cls.FunctionCallResultVMCommand) {
              return true;
            }
          }
        }
        return false;
      }

      /**
       * Checks if there are pending commands in the scheduler
       * @param {String} [classname] - class name of the command
       * @returns {boolean}
       */
      hasPendingCommands(classname) {
        if (!classname) {
          return this._commandsQueue.length > 0;
        }

        for (const cmd of this._commandsQueue) {
          if (cmd.__name === classname) {
            return true;
          }
        }

        return false;
      }

      /**
       * Return true if we are currently replaying a log (from GBC or GDC-UR)
       * @return {boolean|gbc.LogPlayerService._isLogReplay}
       */
      isRunningLog() {
        return window.gbcWrapper._readyData.isLogReplay || context.LogPlayerService.isLogReplay();
      }

      /**
       * @returns {number} the creation time of the last executed command
       */
      getLastCommandTime() {
        return this._lastCommandTime;
      }

      // ============== START - SPECIFIC COMMANDS ===================

      /**
       * Request focus on the given node
       * @param {classes.NodeBase} node - node which should get the focus
       * @param {number} [cursor1] - current starting cursor of the node
       * @param {number} [cursor2] - current ending cursor of the node
       * @param {number} [rowIndex] - hint for current row index requested
       */
      focusVMCommand(node, cursor1, cursor2, rowIndex) {
        return this._addCommand(new cls.FocusVMCommand(this.getApplication(), node, cursor1, cursor2, rowIndex));
      }

      /**
       * Send value on the given node
       * @param {classes.NodeBase} node - target node
       * @param {string} value - current value of the node
       * @param {boolean} [vmSync] - by default command value requires a VM sync
       */
      valueVMCommand(node, value, vmSync = true) {
        return this._addCommand(new cls.ValueVMCommand(this.getApplication(), node, value, vmSync));
      }

      /**
       * Send cursors of a given node
       * @param {classes.NodeBase} node - target node
       * @param {number} cursor1 - current starting cursor of the node
       * @param {number} cursor2 - current ending cursor of the node
       * @param {number} valueLength - node value length
       */
      cursorsVMCommand(node, cursor1, cursor2, valueLength) {
        return this._addCommand(new cls.CursorsVMCommand(this.getApplication(), node, cursor1, cursor2, valueLength));
      }

      /**
       * Send scroll change command. During a scroll
       * @param {classes.TableNode|classes.MatrixNode} node
       * @param {number} offset
       * @returns {classes.ScrollVMCommand} returns the command created and added in the scheduler or null
       */
      scrollVMCommand(node, offset) {
        if (this.isProcessingPageSizeVMCommand()) {
          // if there is a processing pageSize VM command don't send a scroll command because the VM can change
          // the offset in the pageSize command response.
          return null;
        }
        return this._addCommand(new cls.ScrollVMCommand(this.getApplication(), node, offset));
      }

      /**
       * Send pageSize change command.
       * @param {classes.TableNode} node
       * @param {number} pageSize
       * @param {number} bufferSize
       */
      pageSizeVMCommand(node, pageSize, bufferSize) {
        return this._addCommand(new cls.PageSizeVMCommand(this.getApplication(), node, pageSize, bufferSize));
      }

      /**
       * Send currentRow change command
       * @param {classes.TableNode|classes.MatrixNode} node
       * @param {string} actionName
       * @param {boolean} [ctrlKey] - ctrlKey pressed during command creation
       * @param {boolean} [shiftKey] - shiftKey pressed during command creation
       */
      currentRowVMCommand(node, actionName, ctrlKey, shiftKey) {
        return this._addCommand(new cls.CurrentRowVMCommand(this.getApplication(), node, actionName, ctrlKey, shiftKey));
      }

      /**
       * Send rowSelection change command
       * @param {classes.TableNode} node
       * @param {boolean} ctrlKey
       * @param {boolean} shiftKey
       * @param {number} type - type of row selection (merge, toggle, selectAll)
       * @param {string} [actionName] - actionName used to change current row
       */
      rowSelectionVMCommand(node, ctrlKey, shiftKey, type, actionName) {
        return this._addCommand(new cls.RowSelectionVMCommand(this.getApplication(), node, ctrlKey, shiftKey, type, actionName));
      }

      /**
       * Send action command
       * @param {classes.NodeBase} node
       * @param {Object} [options] - add options
       * @param {Boolean} [options.noUserActivity] - true if action is not from a user interaction
       * @param {String} [options.actionName] - action name instead of id
       * @param {boolean} [priority] - true if it is a priority command
       */
      actionVMCommand(node, options = {}, priority = false) {
        return this._addCommand(new cls.ActionVMCommand(this.getApplication(), node, options), true, priority);
      }

      /**
       * Send a front call result
       * @param status front call result status
       * @param message front call result status message
       * @param values front call result values
       */
      functionCallResultVMCommand(status, message, values) {
        // In Logplayer, FunctionCallResult commands are unnecessary and may freeze the whole commands queue in GDC-UR (desynchronization between what is expected between GDC-UR and GBC)
        if (!this.isRunningLog()) {
          return this._addCommand(new cls.FunctionCallResultVMCommand(this.getApplication(), status, message, values), true, true);
        }
        return null;
      }

      /**
       * Send VM event command
       * @param {classes.VMEventBase} event
       * @param {classes.NodeBase} [node]
       * @param {boolean} [needsVmSync]
       */
      eventVMCommand(event, node, needsVmSync = false) {
        const cmd = new cls.EventVMCommand(this.getApplication(), event, node);
        cmd.setNeedsVmSync(needsVmSync);
        return this._addCommand(cmd);
      }

      /**
       * Send layout command.
       * @param {Object} opts
       */
      layoutCommand(opts = {}) {
        if (this._application) {
          const cmd = new cls.LayoutCommand(this.getApplication(), opts);
          context.LogService.scheduler.log("[app" + this._application.applicationHash + "] " + cmd.__name + " command set", cmd);
          if (this._layoutCommand === null) {
            this._layoutCommand = cmd;
          } else {
            this._layoutCommand.merge(cmd);
          }
          this._start(cmd);
          return cmd;
        }
        return null;
      }

      /**
       * Send restore focus command.
       */
      restoreFocusCommand() {
        if (this._application) {
          const cmd = new cls.RestoreFocusCommand(this.getApplication());
          context.LogService.scheduler.log("[app" + this._application.applicationHash + "] " + cmd.__name + " command set", cmd);
          if (this._restoreFocusCommand === null) {
            this._restoreFocusCommand = cmd;
          } else {
            this._restoreFocusCommand.merge(cmd);
          }
          this._start(cmd);
          return cmd;
        }
        return null;
      }

      /**
       * Add a delayedKey command
       * @param {String} keyString string corresponding to the key
       * @param {Event} keyEvent
       */
      delayedKeyCommand(keyString, keyEvent) {
        return this._addCommand(new cls.DelayedKeyCommand(this.getApplication(), keyString, keyEvent));
      }

      /**
       * Add a delayedMouseClick command
       * @param {classes.WidgetBase} widget - widget where we must replay the event
       * @param {Event} mouseEvent
       */
      delayedMouseClickCommand(widget, mouseEvent) {
        return this._addCommand(new cls.DelayedMouseClickCommand(this.getApplication(), widget, mouseEvent));
      }

      /**
       * Add an open drop down command
       * @param {classes.NodeBase} node
       */
      openDropDownCommand(node) {
        return this._addCommand(new cls.OpenDropDownCommand(this.getApplication(), node));
      }

      /**
       * Add an open row bound menu command
       * @param {classes.NodeBase} node
       */
      openRowBoundMenuCommand(node) {
        return this._addCommand(new cls.OpenRowBoundMenuCommand(this.getApplication(), node));
      }

      /**
       * Add a native back command
       * @param {String[]} actionList list of actions
       */
      nativeBackCommand(actionList) {
        return this._addCommand(new cls.NativeBackCommand(this.getApplication(), actionList));
      }

      /**
       * Add a clipboard command for paste
       * @param {String} textToPaste text to paste
       * @param {classes.WidgetBase} [widget]
       */
      clipboardPasteCommand(textToPaste, widget = null, insertPosition = null) {
        return this._addCommand(new cls.ClipboardPasteCommand(this.getApplication(), textToPaste, widget, insertPosition));
      }

      /**
       * Add a clipboard command for cut
       * @param {classes.WidgetBase} widget
       */
      clipboardCutCommand(widget) {
        return this._addCommand(new cls.ClipboardCutCommand(this.getApplication(), widget));
      }

      /**
       * Add a native close command
       */
      nativeCloseCommand() {
        return this._addCommand(new cls.NativeCloseCommand(this.getApplication()));
      }

      /**
       * Add a native notificationpushed command
       */
      nativeNotificationPushedCommand() {
        return this._addCommand(new cls.NativeNotificationPushedCommand(this.getApplication()));
      }

      /**
       * Add a native notificationselected command
       */
      nativeNotificationSelectedCommand() {
        return this._addCommand(new cls.NativeNotificationSelectedCommand(this.getApplication()));
      }

      /**
       * Add a enterbackground command
       */
      enterBackgroundCommand() {
        return this._addCommand(new cls.EnterBackgroundCommand(this.getApplication()));
      }

      /**
       * Add a enterforeground command
       */
      enterForegroundCommand() {
        return this._addCommand(new cls.EnterForegroundCommand(this.getApplication()));
      }

      /**
       * Add an applicationStateChanged command
       */
      applicationStateChangedCommand() {
        return this._addCommand(new cls.ApplicationStateChangedCommand(this.getApplication()));
      }

      /**
       * Add a native cordovacallback command
       */
      nativeCordovaCallbackCommand() {
        return this._addCommand(new cls.NativeCordovaCallbackCommand(this.getApplication()));
      }

      /**
       * Add a callback command
       * @param {function} callback - function to call when command is executed
       * @return {classes.CallbackCommand} callback command created
       */
      callbackCommand(callback) {
        return this._addCommand(new cls.CallbackCommand(this.getApplication(), callback));
      }

      /** Start group command.
       *  all commands added between call of startGroupCommand() and finishGroupCommand()
       *  will be added in a group command.
       */
      startGroupCommand() {
        const groupCommand = new cls.GroupCommand(this.getApplication(), []);
        this._currentGroupCommandQueue.push(groupCommand);
        context.LogService.scheduler.log("[app" + this._application.applicationHash + "] Start command group", groupCommand);
        return groupCommand;
      }

      /** Finish group command and add it in the scheduler queue.
       */
      finishGroupCommand() {
        const groupCommand = this._currentGroupCommandQueue.pop();
        context.LogService.scheduler.log("[app" + this._application.applicationHash + "] Finish command group", groupCommand);
        this._addCommand(groupCommand);
      }

      // ============== END - SPECIFIC COMMANDS ===================

      /**
       * Attach the hook function to schedulerProcessStarted event.
       * That is emitted once the start function is triggered.
       * @param {Function} hook the hook
       * @param {boolean} [once] true to free handle after first call
       * @return {HandleRegistration} the event handle
       */
      schedulerProcessStartedEvent(hook, once) {
        return this.when(context.constants.schedulerEvents.commandProcessStarted, hook, once);
      }

      /**
       * Attach the hook function to schedulerProcessEnded event.
       * That is emitted once the layout and focus had been restored.
       * @param {Function} hook the hook
       * @param {boolean} [once] true to free handle after first call
       * @return {HandleRegistration} the event handle
       */
      schedulerProcessEndedEvent(hook, once) {
        return this.when(context.constants.schedulerEvents.commandProcessEnded, hook, once);
      }
    }

    cls.SchedulerApplicationService = SchedulerApplicationService;
    cls.ApplicationServiceFactory.register("Scheduler", cls.SchedulerApplicationService);
  }
);
